import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';

import FlexWrap from '../common/FlexWrap';
import useRefundCheckDataSubscribe from '@/hooks/useRefundCheckDataSubscribe';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { REFUNDSTATUS } from '@/constants/refund.const';
import { convertToKoreanUnit } from '@/util/format';
import useCreateGlobalPaymentImmediate from '@/hooks/useCreateGlobalPaymentImmediate';
import { updateModal } from '@/store/modules/modal';
import { PAYMENT_STATUS } from '@/constants/payment.const';
import useGlobalPaymentCRUD from '@/hooks/useGlobalPaymentCRUD';
import AliAndWechat from '@/assets/paymentLogos/alipayAndWechat.png';

type Props = {
  btnCallback?: () => void; //버튼 동작: 기본 동작(onClose)+btnCallback 실행
  onClose: () => void;
};

function PaymentCreateModal({ btnCallback, onClose }: Props) {
  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  const { hospitalId } = useAppSelector((state) => state.user);
  const { currentTabletID } = useAppSelector((state) => state.tabletId);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      paymentAmount: '',
    },
  });

  const dispatch = useAppDispatch();
  const { refundInfo } = useRefundCheckDataSubscribe(
    hospitalId.toString(),
    currentTabletID,
  );

  const { deletePayment } = useGlobalPaymentCRUD();
  const { mutateAsync: createGlobalPaymentMutate } =
    useCreateGlobalPaymentImmediate(hospitalId.toString(), currentTabletID);

  const isSent = refundInfo && refundInfo?.status === REFUNDSTATUS.SENT;

  useEffect(() => {
    if (primaryButtonRef.current) {
      primaryButtonRef.current.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      if (e.key === 'Enter') {
        if (btnCallback) {
          btnCallback();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [btnCallback, onClose]);

  useEffect(() => {
    if (isSent && btnCallback) {
      btnCallback();
    }
  }, [isSent, btnCallback]);

  const onSubmit = async (data: { paymentAmount: string }) => {
    const amount = parseInt(data.paymentAmount.replace(/[^0-9]/g, ''));
    const res = await createGlobalPaymentMutate(amount);
    onClose();

    dispatch(
      updateModal({
        isActive: true,
        type: 'PAYMENT_WAIT',
        data: {
          globalPayPrice: convertToKoreanUnit(amount) + '원',
          onClick: () => {},
          onClose: async () => {
            // await updatePayment({
            //   paymentId: res?.id,
            //   newState: PAYMENT_STATUS.CANCELED,
            //   paymentAmount: 0,
            // });
            await deletePayment({ paymentId: res?.id });

            dispatch(
              updateModal({
                isActive: false,
                data: null,
                type: null,
              }),
            );
          },
        },
      }),
    );
  };

  return (
    <Container>
      <Modal>
        <FlexWrap dir='column'>
          <PaymentLogo src={AliAndWechat} alt='' />
          <ModalTitle>결제금액을 입력해주세요</ModalTitle>
        </FlexWrap>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FakeInput>
            <Controller
              name='paymentAmount'
              control={control}
              rules={{
                required: '결제금액이 입력되지 않았습니다',
                validate: (value) => {
                  const numValue = parseInt(value.replace(/[^0-9]/g, ''));
                  return numValue > 0 || '결제금액이 입력되지 않았습니다';
                },
              }}
              render={({ field }) => (
                <input
                  {...field}
                  type='text'
                  value={
                    field.value
                      ? parseInt(
                          field.value.replace(/[^0-9]/g, ''),
                        ).toLocaleString()
                      : ''
                  }
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    field.onChange(value);
                  }}
                  placeholder='금액을 입력하세요'
                />
              )}
            />
            원
          </FakeInput>
          {errors.paymentAmount && (
            <ErrorMessage>{errors.paymentAmount.message}</ErrorMessage>
          )}
          <BtnContainer>
            <Button
              onClick={onClose}
              active={true}
              disabled={false}
              type='button'
            >
              취소
            </Button>
            <Button
              ref={buttonRef}
              type='submit'
              colored
              active={true}
              disabled={false}
            >
              해외 간편 결제하기
            </Button>
          </BtnContainer>
        </Form>
      </Modal>
    </Container>
  );
}
const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 200;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 200px;
`;
const Modal = styled(FlexWrap)`
  padding: 32px;
  flex-direction: column;
  width: 672px;
  background-color: white;
  border-radius: 16px;
  min-width: 400px;
  position: relative;
`;

const PaymentLogo = styled.img`
  height: 34px;
  margin-bottom: 6px;
`;

const ModalTitle = styled.h3`
  margin-bottom: 30px;
  font-size: 21px;
  line-height: 150%;
  font-weight: 700;
  color: #3a3b3e;
`;

const FakeInput = styled.div`
  width: 100%;
  height: 73px;
  padding-bottom: 10px;
  margin-bottom: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  border-bottom: 3px solid #7ca7fa;

  color: var(--Mono-80, #3a3b3e);
  text-align: right;
  font-family: Pretendard;
  font-size: 42px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 63px */
  letter-spacing: -0.84px;

  input {
    width: 500px;
    border: none;
    color: var(--Blue-100, #246cf6);
    font-family: Pretendard;
    font-size: 42px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%; /* 63px */
    letter-spacing: -0.84px;

    &::placeholder {
      color: #cbccce;
    }
  }
`;

const BtnContainer = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
`;

const Button = styled.button<{ colored?: boolean; active: boolean }>`
  height: 52px;
  flex: 1;
  background-color: ${(props) =>
    props.active ? (props.colored ? '#246cf6' : '#E5E6E8') : '#D3E2FD'};
  color: ${(props) =>
    props.active ? (props.colored ? '#fff' : '#3A3B3E') : '#fff'};
  border: 1px solid #246cf6;
  border: 1px solid ${(props) => (props.active ? '#E5E6E8' : '#D3E2FD')};
  border-radius: 8px;
  font-size: 16px;
  line-height: 150%;
  padding: 14px 20px;
  :hover {
    opacity: 0.85;
  }
`;

const ErrorMessage = styled.span`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  text-align: left;
  color: red;
  font-size: 14px;
  margin-top: -32px;
  margin-bottom: 24px;
`;

const Form = styled.form`
  width: 100%;
`;

export default PaymentCreateModal;
